import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Link2, Unlink, ShieldAlert, CheckCircle2 } from "lucide-react";

const ELIGIBLE = new Set(["restaurant", "venue", "catering"]);

/**
 * Vendor-facing Tripleseat card. Shows a friendly "pending partner approval"
 * state until the backend reports configured=true (i.e. NContro has obtained
 * partner OAuth credentials from Tripleseat's Partnership team).
 *
 * When configured but not connected → a "Connect Tripleseat" button appears.
 * When connected → status pill + Sync now + Disconnect.
 *
 * NEVER surfaces tokens, client secrets, or webhook signing keys — those are
 * backend-only and are not exposed by /api/integrations/tripleseat/status.
 */
export default function IntegrationsPanel({ vendors }) {
  const eligibleVendors = (vendors || []).filter(v => ELIGIBLE.has(v.category));
  const [vendorId, setVendorId] = useState(eligibleVendors[0]?.vendor_id || "");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!vendorId) { setStatus(null); return; }
    api.get("/integrations/tripleseat/status", { params: { vendor_id: vendorId } })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId && eligibleVendors[0]?.vendor_id) setVendorId(eligibleVendors[0].vendor_id);
  }, [eligibleVendors, vendorId]);

  const refresh = () =>
    api.get("/integrations/tripleseat/status", { params: { vendor_id: vendorId } })
      .then(({ data }) => setStatus(data))
      .catch(() => {});

  const startConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/tripleseat/start", null, { params: { vendor_id: vendorId } });
      window.location.assign(data.authorize_url);
    } catch (e) {
      const detail = e.response?.data?.detail || "Not available";
      if (e.response?.status === 501) {
        toast.info("Tripleseat is pending partner approval — check back soon.");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setBusy(false); }
  };

  const doSync = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/tripleseat/sync", null, { params: { vendor_id: vendorId } });
      toast.success("Sync started");
    } catch (e) {
      if (e.response?.status === 501) {
        toast.info("Tripleseat is pending partner approval.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setBusy(false); refresh(); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Tripleseat? Your synced events will stop blocking availability. Your manual availability rules are unaffected.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/tripleseat/disconnect", null, { params: { vendor_id: vendorId } });
      toast.success("Disconnected");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (eligibleVendors.length === 0) {
    return (
      <div className="card-elegant p-6 text-sm text-[color:var(--text-muted)]" data-testid="tripleseat-not-eligible">
        Tripleseat is available for restaurant, venue, and catering listings. Add an eligible listing to see integrations.
      </div>
    );
  }

  return (
    <div data-testid="integrations-panel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger data-testid="integrations-vendor-select" className="bg-white h-11 max-w-xs">
            <SelectValue placeholder="Choose a listing" />
          </SelectTrigger>
          <SelectContent>
            {eligibleVendors.map(v => (
              <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tripleseat card */}
      <div className="card-elegant p-6 sm:p-8" data-testid="tripleseat-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Calendar sync</div>
            <div className="font-serif text-3xl">Tripleseat</div>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-lg">
              Automatically block NContro availability when your Tripleseat calendar has a booked (DEFINITE) event. Leads and tentative holds are ignored so your marketplace stays open until you confirm.
            </p>
          </div>
          <StatusPill status={status} />
        </div>

        {/* Body — three states */}
        {status && !status.configured && (
          <div className="mt-6 rounded-md bg-[#F5F2EB] border border-[#E8E2D9] p-4 flex gap-3" data-testid="tripleseat-pending">
            <ShieldAlert className="w-5 h-5 text-[color:var(--accent)] shrink-0 mt-0.5" />
            <div className="text-sm text-[color:var(--text-muted)]">
              <strong className="text-[color:var(--text)]">Not connected — awaiting partner approval.</strong> NContro is applying to Tripleseat's Partnership team for API access. Once approved, this integration will unlock for eligible vendors — no action required from you until then.
            </div>
          </div>
        )}

        {status && status.configured && !status.connected && (
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="tripleseat-connect-cta">
            <Button
              data-testid="tripleseat-connect-btn"
              onClick={startConnect}
              disabled={busy}
              className="btn-sage rounded-full h-11 px-5"
            >
              <Link2 className="w-4 h-4 mr-2" /> Connect Tripleseat
            </Button>
            <span className="text-xs text-[color:var(--text-muted)]">You'll be sent to Tripleseat to authorize NContro.</span>
          </div>
        )}

        {status && status.connected && (
          <div className="mt-6 space-y-4" data-testid="tripleseat-connected">
            <dl className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="eyebrow mb-1">Site</dt>
                <dd className="font-medium">{status.site_id || "—"}</dd>
              </div>
              <div>
                <dt className="eyebrow mb-1">Last sync</dt>
                <dd className="font-medium">{status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "—"}</dd>
              </div>
              <div>
                <dt className="eyebrow mb-1">Blocking events</dt>
                <dd className="font-medium">{status.active_event_count ?? 0}</dd>
              </div>
            </dl>
            {status.last_sync_error && (
              <div className="rounded-md bg-[#C05A46]/10 text-[#C05A46] px-3 py-2 text-xs" data-testid="tripleseat-sync-error">
                Last sync error: {status.last_sync_error}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="tripleseat-sync-btn" variant="outline" className="rounded-full" onClick={doSync} disabled={busy}>
                <RefreshCw className="w-4 h-4 mr-2" /> Sync now
              </Button>
              <Button data-testid="tripleseat-disconnect-btn" variant="ghost" className="rounded-full text-[#C05A46]" onClick={disconnect} disabled={busy}>
                <Unlink className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[#E8E2D9] text-[11px] text-[color:var(--text-muted)]">
          Blocked statuses: {status?.blocking_statuses?.join(", ") || "DEFINITE"} • NContro never stores customer PII from Tripleseat.
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  if (status.connected) {
    return (
      <span data-testid="tripleseat-status-pill" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-[#8A9A86]/20 text-[color:var(--text)] px-2 py-1 rounded-full">
        <CheckCircle2 className="w-3.5 h-3.5" /> Connected
      </span>
    );
  }
  if (!status.configured) {
    return (
      <span data-testid="tripleseat-status-pill" className="text-[10px] uppercase tracking-widest bg-[#F5F2EB] text-[#5C5C5C] px-2 py-1 rounded-full">
        Pending partner approval
      </span>
    );
  }
  return (
    <span data-testid="tripleseat-status-pill" className="text-[10px] uppercase tracking-widest bg-[#EFEADF] text-[#5C5C5C] px-2 py-1 rounded-full">
      Not connected
    </span>
  );
}
